import { useId } from "react";
import { useTranslation } from "react-i18next";
import Input from "@/components/ui/Input";
import Textarea from "@/components/ui/Textarea";
import { Label } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  SUPPORTED_LOCALES,
  localeFlag,
  supportedLanguageLabel,
} from "@/lib/locales";
import type { TranslationFormValues } from "@/lib/translationResolution";

export interface TranslationInputProps {
  label: string;
  values: TranslationFormValues;
  onChange: (values: TranslationFormValues) => void;
  required?: boolean;
  multiline?: boolean;
  placeholder?: string;
  error?: string;
  hint?: string;
  maxLength?: number;
}

export function TranslationInput({
  label,
  values,
  onChange,
  required = false,
  multiline = false,
  placeholder,
  error,
  hint,
  maxLength,
}: TranslationInputProps) {
  const { t } = useTranslation("components");
  const fieldId = useId();
  const InputComponent = multiline ? Textarea : Input;

  const handleChange = (lang: (typeof SUPPORTED_LOCALES)[number], value: string) => {
    onChange({
      ...values,
      [lang]: value,
    });
  };

  const renderCharCounter = (value: string) => {
    if (maxLength === undefined) {
      return null;
    }
    const length = value.length;
    const atLimit = length >= maxLength;
    return (
      <p
        className={cn(
          "mt-1 text-right text-xs",
          atLimit ? "font-medium text-error-600" : "text-surface-500",
        )}
        aria-live="polite"
      >
        {t("translationCharCount", { current: length, max: maxLength })}
      </p>
    );
  };

  return (
    <div className="space-y-3">
      <Label spacing="tight">{label}</Label>
      {required ? (
        <p className="text-xs text-surface-500">{t("translationAllRequired")}</p>
      ) : null}
      {hint ? <p className="text-xs text-surface-500">{hint}</p> : null}
      {error ? <p className="text-sm text-error-600">{error}</p> : null}
      <div className="space-y-3">
        {SUPPORTED_LOCALES.map((code) => {
          const value = values[code] ?? "";
          const inputId = `${fieldId}-${code}`;
          return (
            <div key={code}>
              <InputComponent
                id={inputId}
                label={`${localeFlag(code)} ${supportedLanguageLabel(code)}`}
                value={value}
                onChange={(e) => handleChange(code, e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                required={required}
              />
              {renderCharCounter(value)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
